import React, { createContext, useContext, useState } from "react";

// Mock Context
interface SelectContextType {
  open: boolean;
  setOpen: (open: boolean) => void;
  value: any;
  handleSelect: (value: any) => void;
}

const SelectContext = createContext<SelectContextType | null>(null);

export const Select = ({
  children,
  onValueChange,
  value: controlledValue,
  defaultValue,
}: {
  children: React.ReactNode;
  onValueChange?: (value: string) => void;
  value?: string;
  defaultValue?: string;
}) => {
  const [open, setOpen] = useState(false);
  const [internalValue, setInternalValue] = useState(defaultValue || "");
  
  const value = controlledValue !== undefined ? controlledValue : internalValue;

  const handleSelect = (val: string) => {
    setInternalValue(val);
    if (onValueChange) onValueChange(val);
    setOpen(false);
  };

  return (
    <SelectContext.Provider value={{ open, setOpen, value, handleSelect }}>
      <div className="relative inline-block w-full">{children}</div>
    </SelectContext.Provider>
  );
};

export const SelectTrigger = ({
  children,
  className,
  style,
}: {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}) => {
  const context = useContext(SelectContext);
  if (!context) throw new Error("SelectTrigger must be used within Select");

  return (
    <button
      type="button"
      onClick={() => context.setOpen(!context.open)}
      className={className}
      style={style}
      data-state={context.open ? "open" : "closed"}
    >
      {children}
    </button>
  );
};

export const SelectValue = ({ placeholder }: { placeholder?: string }) => {
  const context = useContext(SelectContext);
  if (!context) throw new Error("SelectValue must be used within Select");
  
  // This is a simplification. In a real Select, we'd look up the label for the value.
  // For now, we'll just display the value or placeholder. 
  // Ideally, the parent component passes the label or we find it in children (complex).
  return <span>{context.value || placeholder}</span>;
};

export const SelectContent = ({
  children,
  className,
  position = "popper",
  side = "bottom",
  sideOffset = 4,
  align = "center",
}: {
  children: React.ReactNode;
  className?: string;
  position?: "popper" | "item-aligned";
  side?: "top" | "right" | "bottom" | "left";
  sideOffset?: number;
  align?: "start" | "center" | "end";
}) => {
  const context = useContext(SelectContext);
  if (!context) throw new Error("SelectContent must be used within Select");

  if (!context.open) return null;

  // Basic positioning logic
  const style: React.CSSProperties = {
    position: 'absolute',
    top: '100%',
    left: 0,
    zIndex: 50,
    marginTop: sideOffset,
    width: '100%',
  };

  return (
    <div className={className} style={style}>
      {children}
    </div>
  );
};

export const SelectItem = ({
  children,
  value,
  className,
}: {
  children: React.ReactNode;
  value: string;
  className?: string;
}) => {
  const context = useContext(SelectContext);
  if (!context) throw new Error("SelectItem must be used within Select");

  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        context.handleSelect(value);
      }}
      className={className}
    >
      {children}
    </div>
  );
};

